import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EntryBucket } from '../api/types';

export interface EntryChartProps {
  byEntry: EntryBucket[] | null;
}

/** Fixed order — a known two-value enum, not sorted by size. */
const ENTRY_ORDER: EntryBucket['entered_via'][] = ['signup', 'settings'];

const ENTRY_LABEL: Record<EntryBucket['entered_via'], string> = {
  signup: 'Signup',
  settings: 'Settings',
};

function fmtNum(v: number): string {
  return v.toLocaleString();
}

interface Row {
  entered_via: EntryBucket['entered_via'];
  label: string;
  counted: number;
  verified: number;
}

interface EntryTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
}

function EntryTooltip({ active, payload }: EntryTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-white font-semibold tabular-nums">{fmtNum(p.counted)} referrals</div>
      <div className="text-gray-400 mt-0.5">
        {fmtNum(p.verified)} verified &middot; {p.label}
      </div>
    </div>
  );
}

/**
 * Entered-via breakdown — the only chart on this page where settings-redeemed
 * referrals appear at all. (They are excluded from `ChannelChart` because
 * `invite_channel` is always null for that path, by design; see migration
 * V159.) A null `by_entry` means the query failed and renders "Not
 * available" — never a flat/zero chart, which would assert "nothing
 * happened" when the truth is "we could not tell."
 */
export function EntryChart({ byEntry }: EntryChartProps): React.ReactElement {
  if (byEntry === null) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-gray-500">
        Not available
      </div>
    );
  }

  const byKey = new Map(byEntry.map((b) => [b.entered_via, b]));
  const rows: Row[] = ENTRY_ORDER.filter((k) => byKey.has(k)).map((k) => {
    const b = byKey.get(k) as EntryBucket;
    return { entered_via: k, label: ENTRY_LABEL[k], counted: b.counted, verified: b.verified };
  });

  if (rows.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-gray-500">
        No referrals in this period.
      </div>
    );
  }

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 8, right: 32, left: 8, bottom: 0 }}
          barCategoryGap="30%"
        >
          <CartesianGrid stroke="#1f2937" horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke="#6b7280" fontSize={11} />
          <YAxis
            type="category"
            dataKey="label"
            stroke="#6b7280"
            fontSize={11}
            width={70}
            tickLine={false}
          />
          <Tooltip content={<EntryTooltip />} cursor={{ fill: '#a78bfa', fillOpacity: 0.08 }} />
          <Bar dataKey="counted" fill="#a78bfa" radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
            <LabelList
              dataKey="counted"
              position="right"
              fill="#e5e7eb"
              fontSize={11}
              formatter={(v: string | number | boolean | null | undefined) =>
                typeof v === 'number' ? v.toLocaleString() : v
              }
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default EntryChart;
